import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import { IS_PRERENDER } from '../utils/prerender';
import './InsufficientCreditsModal.css';

// ── NOT ENOUGH CREDITS ───────────────────────────────────────────────────────
// Shown when the server refuses a generation for want of credits. That case is
// NOT an error in the sense the generic banner means: nothing failed, no
// provider was called, and there is a specific thing the reader can do about
// it. Routing it through the same red "AI rendering failed" message told people
// the product was broken when it was working exactly as designed.
//
// The numbers come from the SERVER's reply (`currentCredits`, `requiredCredits`)
// rather than from a local balance, so the dialog can never quote a figure the
// backend disagrees with — the client's copy may be a moment stale, and the
// server's is the one that refused.
//
// Portalled to <body> like the other overlays here, and skipped during
// prerendering for the same reason they are — see utils/prerender.
export default function InsufficientCreditsModal({ open, currentCredits, requiredCredits, onClose }) {
  const { t } = useT();
  const navigate = useNavigate();

  if (IS_PRERENDER || !open) return null;

  return createPortal(
    <div className="icm-overlay" onClick={onClose} role="presentation">
      <div
        className="icm-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="icm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="icm-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
            strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3.5 11.7 8 16.2 9.7 11.7 11.4 10 15.9 8.3 11.4 3.8 9.7 8.3 8Z" />
            <path d="M17.5 14.2 18.4 16.4 20.6 17.3 18.4 18.2 17.5 20.4 16.6 18.2 14.4 17.3 16.6 16.4Z" />
          </svg>
        </div>

        <h2 className="icm-title" id="icm-title">{t('icm_title')}</h2>
        <p className="icm-body">{t('icm_body')}</p>

        <dl className="icm-figures">
          <div className="icm-fig">
            <dt>{t('icm_have')}</dt>
            <dd>{currentCredits ?? 0}</dd>
          </div>
          <div className="icm-fig icm-fig--need">
            <dt>{t('icm_need')}</dt>
            <dd>{requiredCredits ?? 0}</dd>
          </div>
        </dl>

        <div className="icm-actions">
          <button type="button" className="icm-btn icm-btn--ghost" onClick={onClose}>
            {t('icm_cancel')}
          </button>
          <button
            type="button"
            className="icm-btn icm-btn--buy"
            onClick={() => { onClose?.(); navigate('/plans'); }}
          >
            {t('icm_buy')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
